// Change #100496 - Settings > Jobs > Job Sheets.
//
// Which PDF Designer designs the Job Sheet button on a job offers. A design is
// assigned company-wide (every job) or to one job type; a job gets both. When a
// job has more than one, the Print Job Sheet popup shows a style picker and the
// default prints first (a job type's default beats the company-wide one). With
// nothing assigned the button prints the built-in job sheet.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { FormControlLabel, Checkbox, TextField, MenuItem } from "@mui/material";
import { toast } from "react-toastify";

import { FetchAPI } from "../../../../../helpers/APIdata/fetchAPI";
import TableContainer from "../../../../../Components/Common/TableContainer";
import NoDataContent from "../../../../../Components/Common/NoDataContent";
import DeleteModal from "../../../../../Components/Common/DeleteModal";
import AppModal from "../../../../../Components/Common/AppModal";
import LoadingSpinner from "../../../../../Components/Common/Spinner";

const COMPANY_WIDE = "";

const EMPTY_ASSIGNMENT = {
  PdfTemplate_Id: "",
  JobType_Id: COMPANY_WIDE,
  Label: "",
  FlagDefault: false,
  FlagActive: true,
  Order: "",
};

const isOn = (value) => value === true || String(value) === "1";

const openPdfDesigner = (pdfTemplateId) =>
  window.open(pdfTemplateId ? `/pdf-designer/${pdfTemplateId}` : "/pdf-designer", "_blank");

const JobSheets = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [modal, setModal] = useState(null); // null | { Id?, ...assignment }
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const loadRows = useCallback(async () => {
    const response = await FetchAPI("/jobsheetpdftemplate/list", "GET");
    if (response?.status === 200) {
      setRows(response.jobsheetpdftemplate_list || []);
    } else {
      setRows([]);
      // 409 here is "database update pending" - worth saying once.
      if (response?.message) toast.info(response.message);
    }
    setLoading(false);
  }, []);

  // Designs can be created in the designer tab and come back here, so reload on focus.
  const loadDesigns = useCallback(async () => {
    const response = await FetchAPI("/pdftemplate/list", "GET");
    const list = response?.pdftemplate_list || [];
    setDesigns([...list].sort((a, b) => String(a.Name || "").localeCompare(String(b.Name || ""))));
  }, []);

  useEffect(() => {
    loadRows();
    loadDesigns();
    (async () => {
      const response = await FetchAPI("/jobtype/list", "GET");
      setJobTypes(response?.jobtype_list || []);
    })();
    window.addEventListener("focus", loadDesigns);
    return () => window.removeEventListener("focus", loadDesigns);
  }, [loadRows, loadDesigns]);

  const visibleRows = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      [row.PdfTemplate_Name, row.Label, row.JobType_Name || "Company-wide"].some((value) =>
        String(value || "").toLowerCase().includes(term)
      )
    );
  }, [rows, searchQuery]);

  const submitModal = async () => {
    if (!modal?.PdfTemplate_Id) {
      toast.error("Pick a PDF design");
      return;
    }
    setSaving(true);
    const fields = {
      PdfTemplate_Id: Number(modal.PdfTemplate_Id),
      JobType_Id: modal.JobType_Id === COMPANY_WIDE ? null : Number(modal.JobType_Id),
      Label: modal.Label?.trim() || null,
      FlagDefault: isOn(modal.FlagDefault) ? 1 : 0,
      FlagActive: isOn(modal.FlagActive) ? 1 : 0,
      Order: modal.Order === "" ? null : modal.Order,
    };
    const response = modal.Id
      ? await FetchAPI("/jobsheetpdftemplate/update", "PUT", [{ Id: modal.Id, ...fields }])
      : await FetchAPI("/jobsheetpdftemplate/create", "POST", [fields]);
    setSaving(false);
    if (response?.status === 200) {
      toast.success(modal.Id ? "Job sheet updated" : "Job sheet added");
      setModal(null);
      loadRows();
    } else {
      toast.error(response?.message || "Could not save the job sheet");
    }
  };

  const confirmDelete = async () => {
    const response = await FetchAPI("/jobsheetpdftemplate/delete", "DELETE", [deleteId]);
    setDeleteId(null);
    if (response?.status === 200) {
      toast.success("Job sheet removed");
      loadRows();
    } else {
      toast.error(response?.message || "Could not remove the job sheet");
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Design",
        accessor: "PdfTemplate_Name",
        filterable: false,
        Cell: (item) => {
          const row = item.row.original;
          if (!row.PdfTemplate_Name) {
            return <span className="text-danger">Design deleted - not offered</span>;
          }
          return (
            <div className="text-wrap">
              <button
                type="button"
                className="btn btn-link p-0 text-start"
                onClick={() => openPdfDesigner(row.PdfTemplate_Id)}
              >
                {row.PdfTemplate_Name}
              </button>
              {row.PdfTemplate_Status === "draft" ? (
                <span className="badge bg-warning-subtle text-warning ms-2">Draft</span>
              ) : null}
            </div>
          );
        },
      },
      {
        Header: "Applies to",
        accessor: "JobType_Name",
        filterable: false,
        Cell: (item) =>
          item.row.original.JobType_Id ? (
            <span>
              <i className="ri-price-tag-3-line me-1 text-muted" />
              {item.row.original.JobType_Name || `Job type #${item.row.original.JobType_Id}`}
            </span>
          ) : (
            <span>
              <i className="ri-building-line me-1 text-muted" />
              Company-wide
            </span>
          ),
      },
      {
        Header: "Button label",
        accessor: "Label",
        filterable: false,
        Cell: (item) =>
          item.row.original.Label || (
            <span className="text-muted">{item.row.original.PdfTemplate_Name || "-"}</span>
          ),
      },
      {
        Header: "Default",
        accessor: "FlagDefault",
        filterable: false,
        Cell: (item) =>
          isOn(item.row.original.FlagDefault) ? (
            <span className="badge bg-success-subtle text-success">Default</span>
          ) : (
            <span className="text-muted">No</span>
          ),
      },
      {
        Header: "Active",
        accessor: "FlagActive",
        filterable: false,
        Cell: (item) =>
          isOn(item.row.original.FlagActive) ? "Yes" : <span className="text-muted">No</span>,
      },
      {
        Header: "Order",
        accessor: "Order",
        filterable: false,
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: (item) => (
          <UncontrolledDropdown>
            <DropdownToggle className="btn btn-soft-info btn-icon fs-14" type="button">
              <i className="fa-solid fa-ellipsis" />
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem
                onClick={() =>
                  setModal({
                    ...EMPTY_ASSIGNMENT,
                    Id: item.row.original.Id,
                    PdfTemplate_Id: String(item.row.original.PdfTemplate_Id || ""),
                    JobType_Id: item.row.original.JobType_Id
                      ? String(item.row.original.JobType_Id)
                      : COMPANY_WIDE,
                    Label: item.row.original.Label || "",
                    Order: item.row.original.Order ?? "",
                    FlagDefault: isOn(item.row.original.FlagDefault),
                    FlagActive: isOn(item.row.original.FlagActive),
                  })
                }
              >
                <i className="ri-pencil-fill align-bottom me-2 text-muted" />
                Edit
              </DropdownItem>
              <DropdownItem onClick={() => openPdfDesigner(item.row.original.PdfTemplate_Id)}>
                <i className="ri-artboard-line align-bottom me-2 text-muted" />
                Open in PDF Designer
              </DropdownItem>
              <DropdownItem onClick={() => setDeleteId(item.row.original.Id)}>
                <i className="ri-delete-bin-fill align-bottom me-2 text-muted" />
                Remove
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      },
    ],
    []
  );

  const setField = (key) => (e) => setModal((prev) => ({ ...prev, [key]: e.target.value }));
  const setFlag = (key) => (e) => setModal((prev) => ({ ...prev, [key]: e.target.checked }));

  const scopeName =
    modal?.JobType_Id === COMPANY_WIDE
      ? "company-wide"
      : `for ${jobTypes.find((t) => String(t.Id) === String(modal?.JobType_Id))?.Name || "this job type"}`;

  return (
    <div>
      <p className="text-muted mb-3">
        Choose which PDF Designer designs the <strong>Print Job Sheet</strong> button uses. Assign a
        design company-wide or to a job type. A job offers both, and if it has more than one you can
        pick the style when printing. With nothing assigned, the standard built-in job sheet is used.
      </p>
      <Row className="g-4 align-items-center mb-3">
        <Col sm={3}>
          <div className="search-box">
            <Input
              type="search"
              className="form-control search"
              placeholder="Search for..."
              onChange={(e) => setSearchQuery(e.target.value)}
              value={searchQuery}
            />
            <i className="ri-search-line search-icon"></i>
          </div>
        </Col>
        <div className="col-sm-auto ms-auto d-flex gap-2">
          <button type="button" className="btn btn-soft-secondary" onClick={() => openPdfDesigner()}>
            <i className="ri-artboard-line align-bottom me-1"></i>
            PDF Designer
          </button>
          <button
            type="button"
            className="btn btn-success add-btn"
            onClick={() => setModal({ ...EMPTY_ASSIGNMENT })}
          >
            <i className="ri-add-line align-bottom me-1"></i>
            Add Job Sheet
          </button>
        </div>
      </Row>

      {loading ? (
        <LoadingSpinner />
      ) : visibleRows.length ? (
        <TableContainer
          columns={columns}
          data={visibleRows}
          isGlobalFilter={false}
          isAddUserList={false}
          customPageSize={10}
          className="custom-header-css"
          divClass="table-responsive table-card mb-0"
          tableClass="align-middle table-wrap"
          theadClass="text-muted"
        />
      ) : (
        <NoDataContent
          titleText={searchQuery ? `No Results for: ${searchQuery}` : "No designed job sheets yet"}
          paragraphText={
            searchQuery
              ? `Try adjusting your query: ${searchQuery}`
              : "Design a job sheet in the PDF Designer (the Job Sheet sample is a good start), then add it here."
          }
          useButton={false}
        />
      )}

      <AppModal
        open={!!modal}
        onClose={() => !saving && setModal(null)}
        title={modal?.Id ? "Edit job sheet" : "Add job sheet"}
        headerAccent="indigo"
        size="sm"
        loading={saving}
        submitLabel={modal?.Id ? "Save" : "Add"}
        submitDisabled={!modal?.PdfTemplate_Id}
        onSubmit={submitModal}
      >
        <div className="d-grid gap-3">
          <div className="d-flex gap-2 align-items-start">
            <TextField
              select
              fullWidth
              size="small"
              label="PDF design *"
              value={modal?.PdfTemplate_Id || ""}
              onChange={setField("PdfTemplate_Id")}
              helperText={designs.length ? null : "No designs yet - create one in the PDF Designer"}
            >
              {designs.map((design) => (
                <MenuItem key={design.Id} value={String(design.Id)}>
                  {design.Name}
                  {design.Status === "draft" ? " (draft)" : ""}
                </MenuItem>
              ))}
            </TextField>
            <button
              type="button"
              className="btn btn-soft-secondary btn-icon flex-shrink-0"
              title={modal?.PdfTemplate_Id ? "Edit design" : "Open the PDF Designer"}
              onClick={() => openPdfDesigner(modal?.PdfTemplate_Id)}
            >
              <i className="ri-external-link-line" />
            </button>
          </div>
          <TextField
            select
            fullWidth
            size="small"
            label="Applies to"
            value={modal?.JobType_Id ?? COMPANY_WIDE}
            onChange={setField("JobType_Id")}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value={COMPANY_WIDE}>Company-wide (every job)</MenuItem>
            {jobTypes.map((type) => (
              <MenuItem key={type.Id} value={String(type.Id)}>
                Job type: {type.Name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            size="small"
            label="Button label"
            placeholder="Defaults to the design's name"
            value={modal?.Label || ""}
            onChange={setField("Label")}
            helperText="Shown in the style picker, e.g. Customer copy, Engineer copy"
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Order"
            value={modal?.Order ?? ""}
            onChange={setField("Order")}
          />
          <FormControlLabel
            control={<Checkbox checked={isOn(modal?.FlagDefault)} onChange={setFlag("FlagDefault")} />}
            label={`Default ${scopeName} (printed first)`}
          />
          <FormControlLabel
            control={<Checkbox checked={isOn(modal?.FlagActive)} onChange={setFlag("FlagActive")} />}
            label="Active (offered on the Job Sheet button)"
          />
        </div>
      </AppModal>

      <DeleteModal
        show={!!deleteId}
        onDeleteClick={confirmDelete}
        onCloseClick={() => setDeleteId(null)}
        customText="Remove this design from the Job Sheet button? The design itself is kept in the PDF Designer."
        customButtonText="Yes, Remove It!"
      />
    </div>
  );
};

export default JobSheets;
